/**
 * Careers page copy: EN (careers) and FI (rekry).
 * FI strings from https://identio.fi/rekry/ where available; rest translated.
 */

export const CAREERS_COPY = {
  en: {
    hero: {
      title: "Don't compromise your colours. Join the team.",
      description:
        'Identio is a technological company specialised in consulting. With us, you design and develop digital services—and grow as yourself.',
      primaryCta: 'See open positions',
      primaryHref: '/careers#open-positions',
      secondaryCta: 'Read about ownership',
      secondaryHref: 'https://www.identio.fi/blog/why-is-identio-owned-by-its-employees',
    },
    intro: {
      title: 'Come build a success together',
      body: "Identio is a technological company specialised in consulting. It is fully owned by its creators. With us, you will get to design and develop our clients' digital services. We will always strive to find a project that serves you and combines meaningful challenges with personal professional growth. Our goal is to grow, become more international and compete to win partnerships with global market-leading companies and brands. To get there, we need people who have the courage to look at things from different perspectives. We have to make sure everyone feels safe to bring out their opinions and show their true colours.",
      linkText: 'Read more about ownership',
      linkHref: 'https://www.identio.fi/blog/why-is-identio-owned-by-its-employees',
    },
    culture: {
      title1: 'A stamp on your coffee mug, not your forehead',
      body1:
        'We want your coffee mug to read "Identio" and we hope that you can take sips out of it with an appropriate amount of pride. However, we want you to join our team as you are. Competences are essential in recruiting, but first and foremost, we want to hire humans who also have other interests outside work.',
      linkText: null,
      linkHref: null,
      title2: 'What do you want to be when you become an Identio team member?',
      body2:
        "In this field, we will never be complete professionally. That is why we want to ensure that every member of our team has the opportunity to develop themselves both professionally and as a person. We find it important that you can find self-fulfilment both at work and on your free time.",
    },
    openPositions: {
      title: 'Open positions',
      noJobs:
        "At the moment, we don't have any open positions, but we will be recruiting again soon. Keep us in mind and follow us on LinkedIn and our other social media channels to stay updated on new job opportunities as soon as they become available.",
      note: 'Please note that our open positions are described only in Finnish as we are currently looking for engineers with proficiency to use both English and Finnish in a professional manner.',
      apply: 'Apply',
    },
    benefits: {
      title: 'What can we offer you?',
      linkText: 'Read more about our benefits',
      linkHref: '/blog/this-is-how-identio-takes-care-of-you',
      benefitGroups: [
        { icon: 'Wallet', title: 'Salary & financial benefits', items: ['Lunch benefit on top of your salary (as a benefit or cash, depending on your preference)', 'Flexible Edenred Virike benefit (culture, sports, wellness, massage, dental care, commuting)', 'Phone benefit', 'Bicycle benefit', 'Opportunity for ownership'] },
        { icon: 'Clock', title: 'Flexibility & working environment', items: ['Flexible working hours', "Remote working options, taking into account the customer's situation", 'Office space in central Turku and Helsinki', 'High-quality tools of your choice', 'Software and licenses required for the job'] },
        { icon: 'Heart', title: 'Health & well-being', items: ['Private occupational health care and comprehensive health insurance', 'Leisure accident insurance', 'Refreshments and snacks at the office', 'Joint exercise sessions sponsored by Identio'] },
        { icon: 'Gift', title: 'Bonuses & rewards', items: ['Recruitment bonus', 'Anniversary bonus', 'Sales lead bonus and sales team commission'] },
        { icon: 'TrendingUp', title: 'Development & influence', items: ['Self-development during work hours (task forces, tech afterwork)', "Opportunity to influence the company's development"] },
        { icon: 'Users', title: 'Community & culture', items: ['Your own team within Identio', 'Team evenings and company-wide summer and winter parties', 'Payday breakfast once a month'] },
      ],
    },
    process: {
      title: 'A five-step recruiting process',
      linkText: null,
      linkHref: null,
      steps: [
        {
          title: 'Application received',
          description:
            'The first step in the process is usually submitting an application and expressing interest, either by filling out an application form for an open position or by being recommended internally by an Identio team member. Our goal is to make applying as effortless as possible for you.',
        },
        {
          title: 'Application review',
          description:
            'The recruitment team reviews incoming applications regularly and compares them to the role description and the Ideal Candidate Profile (ICP). At this stage, the team may also give you a brief call to ask a few clarifying questions.',
        },
        {
          title: 'Interviews (usually two rounds)',
          description:
            "If the application matches the role and our needs, we typically invite you to two interviews. In the culture interview, we get to know you and your fit with Identio's culture — and vice versa — while the technical interview assesses your technical skills more deeply through a real-world example task, among other things. Neither interview requires significant preparation on your part. During the culture interview, we'll also walk you through our recruitment process in practice – including the possibility of a tentative job offer.",
        },
        {
          title: 'Decision and job offer',
          description: "After the interviews, the recruitment team evaluates the overall picture and determines the title and salary level in accordance with our pay equity principles. If we choose you, we will first reach out by phone. After that, we will send a job offer via email for your review.",
          additional_text: "In some cases, we make a tentative job offer – meaning we select you before a client project has been confirmed. We want to respect everyone's time, so we commit to actively finding the right project for you. The employment contract is signed once the project is in place."
        },
        {
          title: 'Contract and onboarding',
          description:
            'After accepting the offer, we sign the employment contract electronically. The start date will be agreed when your client project has been confirmed. We then begin preparations as soon as possible to make your start and onboarding as smooth as possible. Welcome to the team!',
        },
      ],
    },
    team: {
      title: 'Meet our team',
      subtitle: 'Insights and stories from our team.',
      readMore: 'Read more',
      viewAll: 'View all insights',
      viewAllHref: '/blog',
      empty: 'No team posts available at the moment. Check back later or visit our blog.',
    },
    finalCta: {
      title: 'The faces behind the mission',
      body: 'From the first line of code to the final pixel—meet the people who make it all happen.',
      button: 'Meet the team',
      href: '/team',
    },
    jobPost: {
      back: 'Back',
      applyTitle: 'Join our team?',
      applyEmail: 'Send your application to',
      benefitsLink: 'Read more about our benefits',
    },
  },
  fi: {
    hero: {
      title: 'Tule sellaisena kuin olet. Liity tiimiin.',
      description:
        'Identio on konsultointiin erikoistunut teknologiayritys, joka on 100% tekijöidensä omistama. Pääset kanssamme suunnittelemaan ja kehittämään asiakkaidemme digitaalisia palveluita.',
      primaryCta: 'Katso avoimet työpaikat',
      primaryHref: '/rekry#open-positions',
      secondaryCta: 'Lue omistajuudesta',
      secondaryHref: '/blogi/miksi-identio-on-tyontekijoidensa-omistama',
    },
    intro: {
      title: 'Mukaan rakentamaan yhteistä menestystä',
      body: 'Identio on konsultointiin erikoistunut teknologiayritys, joka on 100% tekijöidensä omistama. Pääset kanssamme suunnittelemaan ja kehittämään asiakkaidemme digitaalisia palveluita. Pyrimme aina löytämään sinua palvelevan projektin, jossa yhdistyvät mielekkäät haasteet ja henkilökohtainen ammatillinen kehitys. Tavoitteenamme on kasvaa ja kansainvälistyä sekä päästä mukaan kilpailemaan kumppanuuksista maailman johtavien yritysten ja brändien kanssa. Saavuttaaksemme tavoitteemme tarvitsemme ihmisiä, joilla on rohkeutta katsoa asioita eri perspektiiveistä. Meidän on varmistettava, että jokaisella on turvallinen olo olla oma itsensä ja tuoda esiin omat mielipiteensä.',
      linkText: 'Lue omistajuudesta',
      linkHref: '/blogi/miksi-identio-on-tyontekijoidensa-omistama/',
    },
    culture: {
      title1: 'Leima kahvikuppiin – ei otsaan',
      body1:
        'Haluamme kahvikupissasi lukevan Identio ja toivomme sinun hörppivän siitä sopivalla ylpeydellä. Haluamme sinut kuitenkin osaksi tiimiämme juuri sellaisena kuin olet. Osaaminen on rekrytoinnissa keskeisessä roolissa, mutta haluamme palkata ensisijaisesti inhimillisiä ihmisiä, joilla on kiinnostuksen kohteita myös töiden ulkopuolella.',
      linkText: 'Lue kulttuuristamme',
      linkHref: '/blogi/toissa-identiolla-kantavina-teemoina-luottamus-ja-joustavuus/',
      title2: 'Mikä sinusta tulee identiolaisena?',
      body2:
        'Tällä alalla emme ole koskaan ammatillisesti valmiita. Siksi haluamme varmistaa, että jokaisella tiimimme jäsenellä on mahdollisuus kehittää itseään ja kasvaa sekä ammatillisesti että ihmisenä. Meille on tärkeää, että sinä pystyt toteuttamaan itseäsi ja unelmiasi niin töissä kuin vapaa-ajallakin.',
    },
    openPositions: {
      title: 'Avoimet työpaikat',
      noJobs: '',
      note: '',
      apply: 'Hae',
    },
    benefits: {
      title: 'Mitä tarjoamme sinulle?',
      linkText: 'Lue muista eduistamme',
      linkHref: '/blogi/edut-identiolla-nain-panostamme-identiolaisten-hyvinvointiin-ja-jaksamiseen',
      benefitGroups: [
        { icon: 'Wallet', title: 'Palkkaus & taloudelliset edut', items: ['Lounasetu palkan päälle (etuna tai rahana valintasi mukaan)', 'Joustava Virike-etu (kulttuuri, liikunta, hyvinvointi, hieronta, hammashoito, työmatkat)', 'Puhelinetu', 'Polkupyöräetu', 'Mahdollisuus omistajuuteen'] },
        { icon: 'Clock', title: 'Joustavuus & työympäristö', items: ['Liukuva työaika', 'Etätyömahdollisuus asiakkaan tilanteen huomioiden', 'Toimistotilat Turun ja Helsingin keskustoissa', 'Hyvät työvälineet valintasi mukaan', 'Työhön tarvittavat ohjelmistot ja lisenssit'] },
        { icon: 'Heart', title: 'Terveys & hyvinvointi', items: ['Yksityinen työterveys ja laaja terveysvakuutus', 'Vapaa-ajan tapaturmavakuutus', 'Virvokkeet ja välipalat toimistolla', 'Idention sponsoroimat yhteiset liikuntahetket'] },
        { icon: 'Gift', title: 'Bonukset & palkitseminen', items: ['Rekrybonus', 'Anniversary-bonus', 'Sales Lead -bonus ja myyntitiimin provisio'] },
        { icon: 'TrendingUp', title: 'Kehittyminen & vaikuttaminen', items: ['Itsensä kehittäminen työajalla (taskforcet, tech afterwork)', 'Mahdollisuus vaikuttaa yrityksen kehitykseen'] },
        { icon: 'Users', title: 'Yhteisö & kulttuuri', items: ['Oma tiimi Idention sisällä', 'Tiimi-illat sekä koko firman kesä- ja talvijuhlat', 'Payday breakfast, yhteinen aamupala kerran kuussa'] },
      ],
    },
    process: {
      title: '5 askeleen rekrytointiprosessi',
      linkText: 'Lue lisää prosessista',
      linkHref: '/meista',
      steps: [
        {
          title: 'Hakemus sisään',
          description:
            'Ensimmäinen askel prosessissa on useimmiten hakemuksen jättäminen ja kiinnostuksen osoittaminen, joko täyttämällä hakulomake avoimeen paikkaan tai tulemalla suositelluksi sisäisesti jonkun identiolaisen toimesta. Tavoitteemme on pitää hakeminen sinulle mahdollisimman vaivattomana.',
        },
        {
          title: 'Hakemuksen käsittely',
          description:
            'Rekrytointitiimi käy saapuneita hakemuksia läpi säännöllisesti ja vertaa niitä roolikuvaukseen sekä Ideal Candidate Profile (ICP) -kuvaukseen. Tässä vaiheessa tiimi saattaa myös soittaa sinulle lyhyen puhelun tarkentavien kysymysten esittämiseksi.',
        },
        {
          title: 'Haastattelut (yleensä kaksi kierrosta)',
          description:
            'Jos hakemus vastaa haussa olevaa roolia ja tarpeita, kutsumme sinut tyypillisesti kahteen haastatteluun. Kulttuurihaastattelussa tutustumme sinuun ja soveltuvuuteesi Idention kulttuuriin – ja päinvastoin – kun taas teknisessä haastattelussa arvioimme syvemmin teknistä osaamistasi keskustelemalla mm. reaalimaailman esimerkkitehtävästä. Kumpikaan haastattelu ei kuitenkaan vaadi sinulta suurempaa ennakkovalmistautumista. Kulttuurihaastattelussa kerromme myös rekrytointiprosessimme käytännöistä – mukaan lukien mahdollisuudesta ehdolliseen työtarjoukseen.',
        },
        {
          title: 'Päätös ja työtarjous',
          description:
            'Haastattelujen jälkeen rekrytointitiimi arvioi kokonaisuutta ja määrittää tittelin ja palkkatason palkkatasa-arvon periaatteitamme noudattaen. Jos valitsemme sinut, olemme ensin yhteydessä puhelimitse. Tämän jälkeen toimitamme vielä työtarjouksen sähköpostitse tutustuttavaksi.',
          additional_text: 'Joskus teemme ehdollisen työtarjouksen. Se tarkoittaa, että valitsemme sinut jo ennen kuin sinulle on asiakasprojektia tiedossa. Haluamme kunnioittaa molempien aikaa, joten sitoudumme aktiivisesti etsimään sopivan projektin. Työsopimus allekirjoitetaan, kun projekti on varmistunut.',
        },
        {
          title: 'Sopimus ja perehdytys',
          description:
            'Tarjouksen hyväksymisen jälkeen allekirjoitamme työsopimuksen sähköisesti. Vahvistamme aloituspäivän projektin varmistuttua. Tämän jälkeen aloitamme pikimmiten valmistelut jotta työssä aloittaminen ja perehdytys olivat sinulle mahdollisimman sujuvia. Tervetuloa tiimiin!',
        },
      ],
    },
    team: {
      title: 'Tutustu identiolaisiin',
      subtitle: 'Ajatuksia ja tarinoita tiimiltämme.',
      readMore: 'Lue lisää',
      viewAll: 'Katso kaikki ajatukset',
      viewAllHref: '/blogi',
      empty: 'Tiimin blogipostauksia ei ole tällä hetkellä. Tarkista myöhemmin uudelleen tai käy blogissamme.',
    },
    finalCta: {
      title: 'Koodin takana olevat ihmiset',
      body: 'Kasvot, nimet ja sellaiset yksityiskohdat, joita ei löydy CV:stä.',
      button: 'Tutustu tiimiin',
      href: '/tiimi',
    },
    jobPost: {
      back: 'Takaisin',
      applyTitle: 'Mukaan tiimiin?',
      applyEmail: 'Lähetä hakemuksesi osoitteeseen',
      benefitsLink: 'Lue lisää eduistamme',
    },
  },
};
